import { useTheme } from "@mui/material";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Box } from "@/Components/v2/atoms/Box";
import { Typography } from "@/Components/v2/atoms/Typography";

import { LikelihoodSummary } from "./components/likelihood/LikelihoodSummary";
import { useScenarioResultsContext } from "./ScenarioResultsProvider";
import { Divider } from "@/Components/v2/atoms/Divider";
import { BubbleChart } from "@/Components/v2/molecule/BubbleChart/BubbleChart";
import { isEmpty } from "lodash";
import { getBubbleChart } from "./helpers/likelihood-bubble-chart/getBubbleChart";
import {
  ExtendedMarketingRevenueRangeTimeSeries,
  useScenarioTimeSeriesResults,
} from "../../api/v2/getScenarioTimeSeriesResults";
import { ThresholdLines } from "@/Components/v2/molecule/BubbleChart/types";
import { useConfig } from "@/hooks/useConfig";
import { CircularProgress } from "@/Components/v2/atoms/Progress";

// TODO: Change these to the actual thresholds when available in the backend API
// Currently, we mimic the thresholds based on the sample bubble chart in Figma
const getThresholdLines = (
  scenarioTimeSeriesResults: ExtendedMarketingRevenueRangeTimeSeries[]
): ThresholdLines => {
  const maxConfidenceInterval = Math.max(
    ...scenarioTimeSeriesResults.map((result) => result.confidenceInterval ?? 0)
  );
  const maxChangeFromHistoricSpend = Math.max(
    ...scenarioTimeSeriesResults.map((result) => result.changeFromHistoricSpend ?? 0)
  );
  return {
    xAxis: maxConfidenceInterval / 2.5,
    yAxis: maxChangeFromHistoricSpend / 4,
  };
};

export const ScenarioLikelihoodPage = () => {
  const { scenario, isScenarioLoading } = useScenarioResultsContext();
  const { data: scenarioTimeSeriesResults, isLoading: isScenarioTimeSeriesResultsLoading } =
    useScenarioTimeSeriesResults({
      scenarioUuid: scenario?.uuid ?? "",
      groupBy: ["channel"],
    });

  const { config } = useConfig();

  const theme = useTheme();

  if (isScenarioLoading || isScenarioTimeSeriesResultsLoading) {
    return <CircularProgress size={150} sx={{ marginTop: "30rem" }} />;
  }

  if (!scenario) {
    return <div>Scenario not found</div>;
  }

  if (!scenario.risk?.risk || !scenarioTimeSeriesResults) {
    return null;
  }

  const filteredTimeSeriesResults = (scenarioTimeSeriesResults ?? []).filter((result) => {
    return (
      result.spend > 0 &&
      result.confidenceInterval !== undefined &&
      result.changeFromHistoricSpend !== undefined
    );
  });

  const likelihoodPercentage = scenario?.risk?.risk * 100;

  const xAxisLabelFormatter = (value: string) => {
    return config.numberAbbreviator.formatAbbreviatedNumber(Number(value));
  };

  const thresholdLines = getThresholdLines(filteredTimeSeriesResults);

  const { scatterSeries, quadrantLabels, tooltipOptions } =
    getBubbleChart({
      thresholdLines,
      timeSeriesResults: filteredTimeSeriesResults,
      theme,
      numberFormatter: config.numberAbbreviator.formatAbbreviatedNumber,
    }) ?? {};

  return (
    <Stack gap={2} direction="column">
      <LikelihoodSummary likelihoodPercentage={likelihoodPercentage} />
      <Stack
        direction="column"
        border={`1px solid ${theme.palette.divider}`}
        borderRadius={4}
        sx={{ width: "100%", overflow: "hidden" }}
      >
        <Stack p={2} pt={3}>
          <Typography fontSize={20} fontWeight={600}>
            Likelihood by Channel
          </Typography>
          <Divider sx={{ mt: 3 }} />
        </Stack>
        <Stack direction="column" gap={2}>
          {isEmpty(scatterSeries) ? (
            <Box
              height="100%"
              width="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
              paddingY={5}
            >
              <Typography variant="body1">No data available</Typography>
            </Box>
          ) : (
            <>
              <Stack direction="row" justifyContent="end" alignItems="center" pr={2}>
                <Typography
                  variant="body1"
                  sx={{ fontSize: 14, fontWeight: 600, lineSpacing: 0.1 }}
                >
                  Bubble Size = Spend
                </Typography>
              </Stack>
              <BubbleChart
                xAxisLabel="Size of Confidence Interval"
                yAxisLabel="Change from Historic Spend %"
                series={scatterSeries}
                thresholdLines={thresholdLines}
                yAxisLabelFormatter={(value) => `${value}%`}
                xAxisLabelFormatter={xAxisLabelFormatter}
                quadrantLabels={quadrantLabels}
                tooltipOptions={tooltipOptions}
              />
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
